/**
 * Self-hosted LanguageTool plugin for TinyMCE 5. Calls a LanguageTool server
 * for spell + grammar suggestions and uses TinyMCE's native annotations API
 * to render the squiggles and the suggestion popover. Activation is a
 * side-effect import in component.tsx; the plugin self-registers via
 * `tinymce.PluginManager.add` on the global `tinymce` object that the
 * self-hosted bundle puts on window.
 */

interface LtMatch {
  message: string;
  shortMessage?: string;
  replacements: Array<{ value: string }>;
  offset: number;
  length: number;
  rule: { id: string; issueType: string; category: { id: string; name: string } };
}

interface LtResponse {
  matches: LtMatch[];
}

interface LtPluginSettings {
  // eslint-disable-next-line camelcase
  languagetool_url?: string;
  // eslint-disable-next-line camelcase
  languagetool_language?: string;
  // eslint-disable-next-line camelcase
  languagetool_ignore_list?: string[];
}

const ANNOTATION_NAME = 'languagetool';

// Upper bound on the text POSTed to LanguageTool. TEXT_EDITOR_MAX_SIZE is 4 MB,
// far past what an LT server accepts on a single request — an uncapped body
// would 413/timeout on every debounce with no signal beyond a silent error
// event. Capping degrades a very large document to "first N chars checked"
// instead. The cap is a prefix slice, so LT's returned offsets still align with
// the full-document node offsets used by findPosition.
const MAX_CHECK_LENGTH = 20000;

// TinyMCE 5's annotator API doesn't expose a `removeAll`; we strip annotation
// spans by walking the body and unwrapping each (`editor.dom.remove(elm, true)`
// removes the element but keeps its children). This is more robust than
// iterating annotator.getAll() because it also handles spans whose annotation
// state is out of sync with the DOM (e.g. after a paste-from-Word).
const clearAnnotations = (editor: any) => {
  const body = editor.getBody?.() as HTMLElement | null | undefined;
  if (!body) return;
  const spans = body.querySelectorAll<HTMLElement>('span.lt-error');
  spans.forEach((span) => editor.dom.remove(span, true));
};

const isSpelling = (m: LtMatch) => m.rule.issueType === 'misspelling' || m.rule.category.id === 'TYPOS';

// Walk the editor's body, collecting text nodes and their cumulative character
// offsets within a flat string. We separate block-level boundaries with '\n'
// so LT sees sentence/paragraph boundaries the way a reader would.
export const collectText = (body: HTMLElement): { text: string; nodes: Array<{ node: Text; start: number }> } => {
  const nodes: Array<{ node: Text; start: number }> = [];
  let text = '';
  const blockTags = new Set([
    'P',
    'DIV',
    'LI',
    'TR',
    'TD',
    'TH',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'BLOCKQUOTE',
    'BR'
  ]);

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const tn = node as Text;
      if (tn.nodeValue) {
        nodes.push({ node: tn, start: text.length });
        text += tn.nodeValue;
      }

      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const isBlock = blockTags.has(el.tagName);
    if (isBlock && text.length > 0 && !text.endsWith('\n')) text += '\n';
    for (const child of [...el.childNodes]) walk(child);
    if (isBlock && !text.endsWith('\n')) text += '\n';
  };

  walk(body);
  return { text, nodes };
};

// Convert an offset within the flat string into a (node, offset) pair.
export const findPosition = (
  nodes: Array<{ node: Text; start: number }>,
  charOffset: number
): { node: Text; offset: number } | null => {
  for (const entry of nodes) {
    const len = entry.node.nodeValue?.length ?? 0;
    if (charOffset >= entry.start && charOffset <= entry.start + len) {
      return { node: entry.node, offset: charOffset - entry.start };
    }
  }

  return null;
};

interface Debounced<T extends (...args: never[]) => void> {
  (...args: Parameters<T>): void;
  cancel: () => void;
}

const debounce = <T extends (...args: never[]) => void>(fn: T, wait: number): Debounced<T> => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const debounced = (...args: Parameters<T>) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      fn(...args);
    }, wait);
  };

  // Lets the editor's `remove` handler cancel a pending check so it can't fire
  // ~800ms after teardown against a detached editor.
  debounced.cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };

  return debounced;
};

// Skip registration in environments where the global `tinymce` isn't loaded —
// notably Jest/jsdom test runs, where the self-hosted TinyMCE bundle that
// index.html injects on the global is absent. Without this guard, every test
// suite that transitively imports the Editor crashes at module load.
const globalScope = typeof window === 'undefined' ? undefined : (window as unknown as { tinymce?: any });
const tm = globalScope?.tinymce;

// TinyMCE 5 invokes plugin factories with `new`, so this MUST be a regular
// function expression, not an arrow function. Arrow functions throw
// "TypeError: r is not a constructor" inside the minified plugin loader.
tm?.PluginManager.add('languagetool', function (editor: any) {
  const settings = editor.settings as LtPluginSettings;
  // No localhost fallback: if languagetool_url isn't configured we fail closed
  // (runCheck early-returns below) rather than POSTing document text to the
  // user's loopback on every debounce. Local dev supplies the URL via config.
  const apiUrl = settings.languagetool_url;
  const language = settings.languagetool_language ?? 'en-US';
  const ignored = new Set((settings.languagetool_ignore_list ?? []).map((w) => w.toLowerCase()));

  let lastInflight: AbortController | null = null;
  let lastMatchCount = 0;
  // Teardown for the currently-open suggestion popover (removes the element and
  // its document/window/iframe scroll + outside-click listeners). Held at plugin
  // scope so opening a new popover, or destroying the editor, can deterministically
  // tear down the previous one instead of orphaning its listeners.
  let activePopoverCleanup: (() => void) | null = null;
  // Last text successfully checked. Annotating fires TinyMCE change events,
  // which would re-arm the debounce forever (a self-triggered request loop);
  // identical text short-circuits unless the toolbar button forces a check.
  let lastCheckedText: string | null = null;

  // Register annotation type. TinyMCE styles the wrapper with class
  // `mce-annotation` plus `data-mce-annotation="languagetool"`, plus our extras.
  // persistent:false is deliberate — squiggles are presentation only, so
  // TinyMCE's serializer unwraps these spans on getContent() (the value handed
  // to onEditorChange and saved to the API). Otherwise the lt-error /
  // data-lt-* markup would be persisted to the backend and accumulate on every
  // load->save round-trip. The spans still live in the editor DOM for display.
  editor.on('init', () => {
    editor.annotator.register(ANNOTATION_NAME, {
      persistent: false,
      decorate: (_uid: string, data: { message: string; replacements: string; spelling: string }) => ({
        attributes: {
          'data-lt-message': data.message,
          'data-lt-replacements': data.replacements,
          'data-lt-kind': data.spelling
        },
        classes: ['lt-error', data.spelling === 'true' ? 'lt-spelling' : 'lt-grammar']
      })
    });
  });

  const runCheck = async (force = false) => {
    if (!apiUrl) return;
    if (!editor.getBody) return;
    const body = editor.getBody() as HTMLElement | null;
    if (!body) return;
    const { text: fullText } = collectText(body);
    const text = fullText.length > MAX_CHECK_LENGTH ? fullText.slice(0, MAX_CHECK_LENGTH) : fullText;
    if (!text.trim()) {
      editor.undoManager.ignore(() => {
        clearAnnotations(editor);
      });
      lastMatchCount = 0;
      lastCheckedText = text;
      editor.fire('LanguageToolMatchesChanged', { count: 0 });
      return;
    }

    if (!force && text === lastCheckedText) {
      return;
    }

    lastInflight?.abort();
    const controller = new AbortController();
    lastInflight = controller;

    let resp: LtResponse | null = null;
    try {
      const res = await fetch(apiUrl, {
        method: 'POST',
        signal: controller.signal,
        body: new URLSearchParams({ text, language })
      });
      if (!res.ok) {
        editor.fire('LanguageToolMatchesChanged', { count: lastMatchCount, error: true });
        return;
      }

      resp = (await res.json()) as LtResponse;
    } catch (error: unknown) {
      if ((error as { name?: string }).name === 'AbortError') return;
      editor.fire('LanguageToolMatchesChanged', { count: lastMatchCount, error: true });
      return;
    }

    const { matches } = resp;
    let count = 0;

    // Annotation rendering is decoration, not a user edit, so it must not
    // create undo levels. annotator.annotate() internally calls
    // undoManager.transact (one level per match), so without this wrap,
    // applying a correction and the re-check it triggers would stack 6-7 undo
    // steps on top of the edit — making a single correction take that many
    // Ctrl+Z presses to revert. ignore() suppresses level creation for every
    // nested transact in the callback.
    editor.undoManager.ignore(() => {
      // Preserve the caret across the re-annotation. annotator.annotate() moves the
      // selection to each match, so a background re-check would otherwise yank the
      // cursor from where the user was typing. Capture it as an absolute offset into
      // the flat text up front (text content is unchanged by annotation, only
      // wrapped) and map it back via findPosition after — a TinyMCE bookmark does not
      // survive the span-wrapping.
      let caretOffset: number | null = null;
      if (editor.hasFocus?.()) {
        const sel = editor.selection.getRng();
        const entry = collectText(body).nodes.find((e) => e.node === sel.startContainer);
        if (entry) caretOffset = entry.start + Number(sel.startOffset);
      }

      clearAnnotations(editor);
      for (const m of matches) {
        const word = text.slice(m.offset, m.offset + m.length);
        if (ignored.has(word.toLowerCase())) continue;

        // Re-walk the DOM for every match: wrapping the previous match in an
        // annotation span splits text nodes, which invalidates earlier
        // (node, offset) positions. The flat-string offsets stay valid
        // because annotations never change textContent.
        const freshNodes = collectText(body).nodes;
        const startPos = findPosition(freshNodes, m.offset);
        const endPos = findPosition(freshNodes, m.offset + m.length);
        if (!startPos || !endPos) continue;

        try {
          const range = editor.dom.createRng();
          range.setStart(startPos.node, startPos.offset);
          range.setEnd(endPos.node, endPos.offset);
          editor.selection.setRng(range);
          editor.annotator.annotate(ANNOTATION_NAME, {
            message: m.message,
            replacements: JSON.stringify(m.replacements.map((r) => r.value).slice(0, 5)),
            spelling: isSpelling(m) ? 'true' : 'false'
          });
          count++;
        } catch {
          // Range setting can throw if a node was detached mid-edit. Skip and continue.
        }
      }

      // Restore the caret to where the user left it (mapped back through the freshly
      // annotated DOM); fall back to collapsing if it wasn't captured (editor unfocused).
      if (caretOffset === null) {
        editor.selection.collapse(true);
      } else {
        const pos = findPosition(collectText(body).nodes, caretOffset);
        if (pos) {
          const caretRange = editor.dom.createRng();
          caretRange.setStart(pos.node, pos.offset);
          caretRange.collapse(true);
          editor.selection.setRng(caretRange);
        }
      }
    });

    lastMatchCount = count;
    lastCheckedText = text;
    editor.fire('LanguageToolMatchesChanged', { count });
  };

  const debouncedCheck = debounce(runCheck, 800);
  editor.on('input change SetContent', () => {
    debouncedCheck();
  });
  editor.on('init', () => {
    debouncedCheck();
  });

  // Toolbar button: re-check on demand.
  editor.ui.registry.addButton('languagetool', {
    icon: 'spell-check',
    tooltip: 'Check spelling',
    onAction: async () => runCheck(true)
  });

  // Replace an annotation span with the chosen suggestion. Wrapped in
  // `undoManager.transact` so the swap is a single undoable step (a raw
  // `node.replaceWith` mutates the DOM behind TinyMCE's back and corrupts the
  // undo stack). Firing `input` re-arms the debounced check so the corrected
  // text is re-validated.
  const applySuggestion = (target: HTMLElement, value: string) => {
    editor.undoManager.transact(() => {
      // A grammar match spanning multiple text nodes/blocks is rendered as
      // several annotation spans sharing one uid. The suggestion replaces the
      // whole matched range, so swap the first span for the replacement text and
      // drop the rest; replacing only `target` would leave the other spans'
      // stale text behind. Single-word (spelling) matches have one span.
      const uid = target.getAttribute('data-mce-annotation-uid');
      let spans: HTMLElement[] = [target];
      if (uid) {
        const body = editor.getBody?.() as HTMLElement | null | undefined;
        const matched = body?.querySelectorAll<HTMLElement>(`.lt-error[data-mce-annotation-uid="${uid}"]`);
        if (matched && matched.length > 0) spans = [...matched];
      }

      const textNode = editor.getDoc().createTextNode(value);
      spans[0].replaceWith(textNode);
      spans.slice(1).forEach((span) => {
        span.remove();
      });
      const range = editor.dom.createRng();
      range.setStartAfter(textNode);
      range.collapse(true);
      editor.selection.setRng(range);
    });
    editor.nodeChanged();
    editor.fire('input');
  };

  // Register the annotation listener — when caret is inside an annotation,
  // pop a small UI offering replacements.
  editor.on('init', () => {
    editor.annotator.annotationChanged(ANNOTATION_NAME, (state: boolean, _name: string, data: any) => {
      if (!state || !data) return;
      const node = editor.selection.getNode() as HTMLElement;
      const target = node.closest('.lt-error');
      if (!target) return;
      const replacementsAttr = target.getAttribute('data-lt-replacements') ?? '[]';
      const message = target.getAttribute('data-lt-message') ?? '';
      let replacements: string[] = [];
      try {
        replacements = JSON.parse(replacementsAttr) as string[];
      } catch {
        replacements = [];
      }

      // Build a quick-pick popover via TinyMCE's notification API. The native
      // contextform requires plugin-specific config that's awkward for dynamic
      // option lists, so we use a lightweight floating menu instead.
      // Tear down any popover still open (element + its listeners) before
      // building the replacement, rather than just orphaning the old element.
      activePopoverCleanup?.();

      // The annotated span lives inside TinyMCE's iframe; getBoundingClientRect()
      // returns coordinates relative to the iframe's viewport. We need to add
      // the iframe's own viewport offset to position the popover correctly in
      // the parent document.
      const iframe = editor.getContentAreaContainer().querySelector('iframe') as HTMLIFrameElement | null;
      const iframeRect = iframe?.getBoundingClientRect();
      const rect = target.getBoundingClientRect();
      // Word position in the parent viewport (the span lives inside the iframe,
      // so its rect is iframe-relative — add the iframe's own viewport offset).
      const anchorTop = (iframeRect?.top ?? 0) + rect.top;
      const anchorBottom = (iframeRect?.top ?? 0) + rect.bottom;
      const anchorLeft = (iframeRect?.left ?? 0) + rect.left;
      const popover = document.createElement('div');
      popover.className = 'lt-suggestion-popover';
      // a11y: expose as a menu so screen readers announce it, and so keyboard
      // users can Escape out / tab through the suggestion buttons.
      popover.setAttribute('role', 'menu');
      popover.setAttribute('aria-label', message || 'Spelling suggestions');
      // Positioned after append once we can measure it (see below). Hidden until
      // then so it can't flash at the top-left corner.
      popover.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        visibility: hidden;
        z-index: 10000;
        background: white;
        border: 1px solid #ccc;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.12);
        font-size: 13px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        max-width: 280px;
        min-width: 160px;
      `;

      const header = document.createElement('div');
      header.style.cssText = 'padding: 8px 12px; border-bottom: 1px solid #eee; color: #555;';
      header.textContent = message;
      popover.append(header);

      if (replacements.length === 0) {
        const empty = document.createElement('div');
        empty.style.cssText = 'padding: 8px 12px; color: #999; font-style: italic;';
        empty.textContent = 'No suggestions';
        popover.append(empty);
      } else {
        // LanguageTool returns replacements that are empty (delete a repeated
        // word) or whitespace-only (collapse a run of spaces to one). Rendered
        // verbatim those look like a blank row, so show a readable label while
        // still applying the real value on click.
        const labelFor = (value: string): string => {
          if (value === '') return '(delete)';
          if (value.trim() === '') return value.length === 1 ? '(single space)' : `(${value.length} spaces)`;
          return value;
        };

        replacements.forEach((r) => {
          const item = document.createElement('button');
          item.type = 'button';
          item.setAttribute('role', 'menuitem');
          item.style.cssText =
            'display: block; width: 100%; padding: 6px 12px; border: 0; background: white; text-align: left; cursor: pointer; font-size: 13px;';
          item.textContent = labelFor(r);
          item.addEventListener('mouseenter', () => {
            item.style.background = '#f0f0f0';
          });
          item.addEventListener('mouseleave', () => {
            item.style.background = 'white';
          });
          item.addEventListener('click', () => {
            applySuggestion(target as HTMLElement, r);
            // closePopover (declared below, hoisted) also detaches the scroll /
            // outside-click listeners; a bare popover.remove() would leave them
            // attached until a later event.
            closePopover();
          });
          popover.append(item);
        });
      }

      document.body.append(popover);

      // Position now that the popover has dimensions. Default below the word;
      // if that would clip against the bottom of the viewport and there's more
      // room above, flip above it instead. Clamp horizontally so a word near
      // the right edge doesn't push the menu off-screen.
      const margin = 8;
      const { height: popoverHeight, width: popoverWidth } = popover.getBoundingClientRect();
      const spaceBelow = window.innerHeight - anchorBottom;
      const placeAbove = spaceBelow < popoverHeight + margin && anchorTop > spaceBelow;
      const preferredTop = placeAbove ? anchorTop - popoverHeight - 4 : anchorBottom + 4;
      // Hard clamp on both axes so the popover is always fully on-screen — the
      // belt to the flip heuristic's suspenders. Without this, a word in the
      // bottom-right corner (where neither above nor below has full room) still
      // clipped: pull the top up so its bottom edge stays inside the viewport,
      // and never let the top go above the viewport.
      const maxTop = window.innerHeight - popoverHeight - margin;
      const resolvedTop = Math.max(margin, Math.min(preferredTop, maxTop));
      const resolvedLeft = Math.max(margin, Math.min(anchorLeft, window.innerWidth - popoverWidth - margin));
      popover.style.top = `${resolvedTop}px`;
      popover.style.left = `${resolvedLeft}px`;
      popover.style.visibility = 'visible';

      // The popover is fixed-positioned from a one-time getBoundingClientRect,
      // so any scroll leaves it stranded. Tear it down on scroll (parent page
      // and the editor iframe) and on an outside click.
      const iframeWin = iframe?.contentWindow;
      function closePopover() {
        popover.remove();
        document.removeEventListener('mousedown', onDocMouseDown, true);
        document.removeEventListener('keydown', onDocKeyDown, true);
        window.removeEventListener('scroll', closePopover, true);
        iframeWin?.removeEventListener('scroll', closePopover, true);
        if (activePopoverCleanup === closePopover) activePopoverCleanup = null;
      }

      function onDocMouseDown(e: Event) {
        if (!popover.contains(e.target as Node)) closePopover();
      }

      // a11y: Escape closes the menu and returns focus to the editor. The
      // suggestion buttons are real <button>s (role=menuitem above), so they're
      // reachable via Tab and activatable with Enter/Space without extra wiring.
      function onDocKeyDown(e: KeyboardEvent) {
        if (e.key === 'Escape') {
          closePopover();
          editor.focus();
        }
      }

      // Defer to next tick so the click that opened the popover doesn't close it.
      activePopoverCleanup = closePopover;
      setTimeout(() => {
        // If the popover was torn down before this tick (e.g. an immediate
        // suggestion click), don't attach listeners referencing a detached
        // element — they would otherwise linger until the next scroll/click.
        if (!popover.isConnected) return;
        document.addEventListener('mousedown', onDocMouseDown, true);
        document.addEventListener('keydown', onDocKeyDown, true);
        window.addEventListener('scroll', closePopover, true);
        iframeWin?.addEventListener('scroll', closePopover, true);
      }, 0);
    });
  });

  // Cleanup any stray popover when the editor is destroyed. activePopoverCleanup
  // detaches the open popover's listeners; the querySelectorAll sweep is a
  // backstop for any element that outlived its cleanup handle.
  editor.on('remove', () => {
    debouncedCheck.cancel();
    activePopoverCleanup?.();
    document.querySelectorAll('.lt-suggestion-popover').forEach((el) => {
      el.remove();
    });
    lastInflight?.abort();
  });

  return {
    getMetadata: () => ({
      name: 'LanguageTool',
      url: 'https://languagetool.org/'
    })
  };
});

export {};
